import { type FC, lazy, Suspense } from "react"

import type { LandingPageTwoColumnsBlock } from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import VideoPreview from "./VideoPreview"

const RichTextPreview = lazy(() => import("@forento/ui/components/RichTextPreview"))

type Props = {
	block: LandingPageTwoColumnsBlock
}

const TwoColumnsBlockPreview: FC<Props> = ({ block }) => {
	const isInverted = block.content.inverted
	const media =
		block.content.media ?? (block.content.image ? { type: "image" as const, ...block.content.image } : null)

	return (
		<section className="px-6 pt-16 pb-0 sm:pt-20 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<div
					className={cn(
						"grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12",
						isInverted && "lg:grid-flow-dense",
					)}
				>
					<div
						className={cn(
							"relative",
							media?.type === "image" && "lg:min-h-96 lg:self-stretch",
							isInverted && "lg:col-start-2",
						)}
					>
						{media?.type === "image" ? (
							<img
								src={media.url}
								alt={media.alt ?? ""}
								className="h-auto max-h-96 w-full max-w-full object-contain lg:absolute lg:inset-0 lg:h-full lg:max-h-none"
							/>
						) : media?.type === "youtube" || media?.type === "vimeo" ? (
							<VideoPreview
								type={media.type}
								videoId={media.videoId}
								className="bg-landing-surface-muted aspect-video w-full rounded-lg"
							/>
						) : null}
					</div>
					<div className={cn("flex flex-col justify-center", isInverted && "lg:col-start-1 lg:row-start-1")}>
						<h2 className="text-landing-foreground text-3xl font-bold tracking-tight sm:text-4xl">
							{block.content.title || "Title"}
						</h2>
						<Suspense>
							<RichTextPreview
								value={block.content.description}
								fallback="Description"
								className="text-landing-foreground-muted mt-4 text-lg leading-8 [&>p]:mb-4 [&>p:last-child]:mb-0"
							/>
						</Suspense>
						{block.content.button && (
							<div className="mt-8">
								<span className="landing-button inline-flex items-center justify-center px-6 py-3 text-base font-semibold">
									{block.content.button.label}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}

export default TwoColumnsBlockPreview
